import {MvsCoreService} from "../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Subscription} from "rxjs";
import {CbWorkbenchService} from "../../service/cb-workbench.service";
import {CbEntityCoverage, CbModuleCoverage} from "../../model/canvas/cb-coverage-node.interface";
import {MvsMessageService} from "../../../../core/shared/service/message/mvs-message.service";
import {CbDomainConceptDto} from "../../model/dto/entity/cb-domain-concept.dto";
import {CbSynonymDto} from "../../model/dto/entity/cb-synonym.dto";
import {ObjectRequestList} from "../../../../core/shared/dto/object/request/object-request-list";
import {MvsUiObjectService} from "../../../../core/shared/object/service/mvs-ui-object.service";
import {MvsCrudModeEnum} from "../../../../core/shared/service/crud/mvs-crud-mode.enum";
import {CbMetaEntityDto} from "../../model/dto/entity/cb-meta-entity.dto";

/**
 * Workbench view tabs.
 */
export enum CbWorkbenchView {
    coverage = 'coverage',
    concepts = 'concepts',
    templates = 'templates',
    rules = 'rules',
    training = 'training',
    tenantSync = 'tenantSync'
}

/**
 * CB Workbench Component.
 * Main orchestrator for the CB configuration workbench.
 * Uses CbMeta* services for metadata management.
 */
@Component({
    selector: 'cb-workbench',
    templateUrl: './cb-workbench.component.html',
    styleUrls: ['./cb-workbench.component.scss'],
    standalone: false
})
export class CbWorkbenchComponent implements OnInit, OnDestroy {

    conceptService: MvsCrudService;
    synonymService: MvsCrudService;
    // Current view
    activeView: CbWorkbenchView = CbWorkbenchView.coverage;

    // View enum for template
    readonly CbWorkbenchView = CbWorkbenchView;

    // Navigation items
    navigationItems = [
        {
            id: CbWorkbenchView.coverage,
            label: 'Schema Coverage',
            icon: 'fa-regular fa-chart-pie',
            tooltip: 'View and manage entity documentation coverage'
        },
        {
            id: CbWorkbenchView.concepts,
            label: 'Domain Concepts',
            icon: 'fa-regular fa-book',
            tooltip: 'Manage domain concepts and synonyms'
        },
        {
            id: CbWorkbenchView.templates,
            label: 'Query Templates',
            icon: 'fa-regular fa-code',
            tooltip: 'Create and manage query templates'
        },
        {
            id: CbWorkbenchView.rules,
            label: 'Business Rules',
            icon: 'fa-regular fa-scale-balanced',
            tooltip: 'Define business rules and filters'
        },
        {
            id: CbWorkbenchView.training,
            label: 'Training Pipeline',
            icon: 'fa-regular fa-graduation-cap',
            tooltip: 'Manage feedback and training data'
        },
        {
            id: CbWorkbenchView.tenantSync,
            label: 'Tenant Sync',
            icon: 'fa-regular fa-cloud-arrow-down',
            tooltip: 'Synchronize documentation from master tenant'
        }
    ];

    // Coverage data
    coverageData: CbModuleCoverage[] = [];
    loading: boolean = false;

    // Selected entity for editing
    selectedEntity: CbEntityCoverage | null = null;
    showEntityEditor: boolean = false;

    // Domain concepts data
    concepts: CbDomainConceptDto[] = [];
    synonyms: CbSynonymDto[] = [];
    conceptsLoading: boolean = false;

    // Concept editor
    selectedConcept: CbDomainConceptDto | null = null;
    showConceptEditor: boolean = false;
    isNewConcept: boolean = true;

    // Statistics
    overallCoverage: number = 0;
    totalModules: number = 0;
    totalEntities: number = 0;
    documentedEntities: number = 0;

    private subscriptions: Subscription[] = [];

    constructor(
        protected coreService: MvsCoreService,
        
        private workbenchService: CbWorkbenchService,
        private messageService: MvsMessageService,
        private objectService: MvsUiObjectService
    ) {
        this.conceptService = this.coreService.getCrudService("cb.CbConcept");

        this.synonymService = this.coreService.getCrudService("cb.CbSynonym");

    }

    ngOnInit(): void {
        // Subscribe to coverage data
        this.subscriptions.push(
            this.workbenchService.coverageData$.subscribe(data => {
                this.coverageData = data;
                this.calculateStatistics();
            })
        );

        // Subscribe to loading state
        this.subscriptions.push(
            this.workbenchService.loading$.subscribe(loading => {
                this.loading = loading;
            })
        );

        // Subscribe to selected entity
        this.subscriptions.push(
            this.workbenchService.selectedEntity$.subscribe(entity => {
                this.selectedEntity = entity;
                this.showEntityEditor = entity !== null;
            })
        );

        // Load initial data
        this.loadCoverageData();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Load coverage data.
     */
    loadCoverageData(): void {
        this.workbenchService.fetchCoverageData().subscribe();
    }

    /**
     * Refresh coverage data.
     */
    refreshCoverage(): void {
        this.workbenchService.refreshCoverage().subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Refreshed', 'Coverage data updated');
            },
            error: () => {
                this.messageService.showErrorMessage('Error', 'Failed to refresh coverage');
            }
        });
    }

    /**
     * Calculate overall statistics.
     */
    private calculateStatistics(): void {
        this.totalModules = this.coverageData.length;
        this.totalEntities = this.coverageData.reduce((sum, m) => sum + m.totalEntities, 0);
        this.documentedEntities = this.coverageData.reduce((sum, m) => sum + m.documentedEntities, 0);
        this.overallCoverage = this.totalEntities > 0
            ? Math.round((this.documentedEntities / this.totalEntities) * 100)
            : 0;
    }

    /**
     * Switch to a different view.
     */
    switchView(view: CbWorkbenchView): void {
        this.activeView = view;

        // Load data for the view if needed
        if (view === CbWorkbenchView.concepts && this.concepts.length === 0) {
            this.loadConceptsData();
        }
    }

    /**
     * Handle entity selection from coverage table.
     * Opens the entity metadata via mvs-object dialog using CbMetaEntity.
     */
    onEntitySelected(entity: CbEntityCoverage): void {
        if (entity.documentationId) {
            // Open existing metadata
            this.objectService.openObjectViaDialog(
                null,
                'cb.CbMetaEntity',
                entity.documentationId,
                null,
                false,
                false,
                () => {
                    this.refreshCoverage();
                },
                MvsCrudModeEnum.update
            );
        } else {
            // Create new metadata for this entity
            const defaultDto = new CbMetaEntityDto();

            this.objectService.openObjectViaDialog(
                null,
                'cb.CbMetaEntity',
                0,
                defaultDto,
                false,
                false,
                () => {
                    this.refreshCoverage();
                },
                MvsCrudModeEnum.create
            );
        }
    }

    /**
     * Get coverage severity class.
     */
    getCoverageSeverity(percent: number): string {
        if (percent >= 80) return 'success';
        if (percent >= 50) return 'warning';
        return 'danger';
    }

    // =====================================
    // Domain Concepts Methods
    // =====================================

    /**
     * Load domain concepts and synonyms.
     */
    loadConceptsData(): void {
        this.conceptsLoading = true;

        forkJoin({
            concepts: this.conceptService.list(ObjectRequestList.createBasic(false, [], [])),
            synonyms: this.synonymService.list(ObjectRequestList.createBasic(false, [], []))
        }).subscribe({
            next: (result) => {
                this.concepts = result.concepts.entries as CbDomainConceptDto[];
                this.synonyms = result.synonyms.entries as CbSynonymDto[];
                this.conceptsLoading = false;
            },
            error: (error) => {
                console.error('Error loading concepts:', error);
                this.conceptsLoading = false;
                // Use mock data for development
                this.concepts = this.getMockConcepts();
                this.synonyms = this.getMockSynonyms();
            }
        });
    }

    /**
     * Handle concept selection from graph.
     */
    onConceptSelected(concept: CbDomainConceptDto): void {
        this.selectedConcept = concept;
    }

    /**
     * Handle concept double-click (open editor).
     */
    onConceptDoubleClicked(concept: CbDomainConceptDto): void {
        this.selectedConcept = concept;
        this.isNewConcept = false;
        this.showConceptEditor = true;
    }

    /**
     * Handle add concept request.
     */
    onAddConceptRequested(): void {
        this.selectedConcept = null;
        this.isNewConcept = true;
        this.showConceptEditor = true;
    }

    /**
     * Close concept editor.
     */
    closeConceptEditor(): void {
        this.showConceptEditor = false;
        this.selectedConcept = null;
    }

    /**
     * Handle concept saved.
     */
    onConceptSaved(concept: CbDomainConceptDto): void {
        this.closeConceptEditor();
        this.loadConceptsData();
    }

    /**
     * Mock concepts for development.
     */
    private getMockConcepts(): CbDomainConceptDto[] {
        return [
            { id: 1, name: 'Active Contract', term: 'Active Contract', definition: 'A contract with status ACTIVE that is currently in effect', category: 'Contract Management' } as CbDomainConceptDto,
            { id: 2, name: 'Customer', term: 'Customer', definition: 'A person or company that has contracts with the organization', category: 'Customer Management' } as CbDomainConceptDto,
            { id: 3, name: 'Premium Payment', term: 'Premium Payment', definition: 'Regular payment made by customer for their contract', category: 'Billing' } as CbDomainConceptDto,
            { id: 4, name: 'Insurance Agent', term: 'Insurance Agent', definition: 'A sales representative who manages customer relationships', category: 'Agent Management' } as CbDomainConceptDto,
            { id: 5, name: 'Policy Document', term: 'Policy Document', definition: 'Legal document outlining contract terms and conditions', category: 'Document Management' } as CbDomainConceptDto,
            { id: 6, name: 'Claim', term: 'Claim', definition: 'A request for payment under the terms of a contract', category: 'Contract Management' } as CbDomainConceptDto,
            { id: 7, name: 'Beneficiary', term: 'Beneficiary', definition: 'Person designated to receive benefits from a contract', category: 'Customer Management' } as CbDomainConceptDto,
            { id: 8, name: 'Invoice', term: 'Invoice', definition: 'Document requesting payment for services or premiums', category: 'Billing' } as CbDomainConceptDto,
            { id: 9, name: 'Broker', term: 'Broker', definition: 'Independent agent who represents multiple companies', category: 'Agent Management' } as CbDomainConceptDto,
            { id: 10, name: 'Contract Renewal', term: 'Contract Renewal', definition: 'Process of extending an existing contract for another term', category: 'Contract Management' } as CbDomainConceptDto
        ];
    }

    /**
     * Mock synonyms for development.
     */
    private getMockSynonyms(): CbSynonymDto[] {
        return [
            { id: 1, conceptDtoId: 1, synonym: 'Running Contract', type: 0 } as CbSynonymDto,
            { id: 2, conceptDtoId: 1, synonym: 'Valid Contract', type: 3 } as CbSynonymDto,
            { id: 3, conceptDtoId: 2, synonym: 'Client', type: 0 } as CbSynonymDto,
            { id: 4, conceptDtoId: 2, synonym: 'Policyholder', type: 3 } as CbSynonymDto,
            { id: 5, conceptDtoId: 3, synonym: 'Insurance Premium', type: 0 } as CbSynonymDto,
            { id: 6, conceptDtoId: 4, synonym: 'Sales Agent', type: 0 } as CbSynonymDto,
            { id: 7, conceptDtoId: 6, synonym: 'Insurance Claim', type: 0 } as CbSynonymDto,
            { id: 8, conceptDtoId: 10, synonym: 'Policy Renewal', type: 0 } as CbSynonymDto
        ];
    }
}
